package com.steamstreet.vegasful.browser.public

import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.appScope
import com.steamstreet.vegasful.browser.spinnerSvg
import com.steamstreet.vegasful.css.css
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.css.em
import kotlinx.css.fontSize
import kotlinx.html.dom.append
import kotlinx.html.js.span
import kotlinx.html.unsafe
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.url.URL
import kotlin.time.Duration.Companion.milliseconds

/**
 * Initialize the global search
 */
fun globalSearch(element: Element) {
    val input = (element.querySelector("input") as? HTMLInputElement)
    fun navigateToSearch(text: String) {
        document.location?.href = "${document.location?.origin}/search?searchText=${encodeURIComponent(text)}"
    }

    // Render the suggestion dropdown as a direct child of <body> (a "portal") so it escapes any
    // ancestor stacking context or overflow clipping and reliably floats above page content.
    // Its position is derived from the input's viewport rect each time it is shown.
    val dropdown = (document.createElement("div") as HTMLElement).apply {
        setAttribute("style", "position:fixed;z-index:2147483000;display:none;")
    }
    document.body?.appendChild(dropdown)

    fun positionDropdown() {
        val rect = (input ?: return).getBoundingClientRect()
        dropdown.style.left = "${rect.left}px"
        dropdown.style.top = "${rect.bottom}px"
        dropdown.style.width = "${rect.width}px"
    }

    fun hideDropdown() {
        dropdown.style.display = "none"
    }

    fun showDropdown() {
        if (dropdown.querySelector(".typeahead-row") != null) {
            positionDropdown()
            dropdown.style.display = "block"
        }
    }

    // Debounce keystrokes so we hit the typeahead endpoint at most once per pause in typing.
    var searchJob: Job? = null
    fun scheduleTypeahead(text: String) {
        searchJob?.cancel()
        if (text.length < 2) {
            dropdown.innerHTML = ""
            hideDropdown()
            return
        }
        searchJob = appScope.launch {
            delay(250.milliseconds)
            val html = Account.load("/search/typeahead?searchText=${encodeURIComponent(text)}")
            dropdown.innerHTML = html ?: ""
            // The snippet always comes back as a full document; only show the dropdown when it
            // actually contains suggestion rows, otherwise it would render as an empty box.
            if (dropdown.querySelector(".typeahead-row") != null) {
                showDropdown()
            } else {
                dropdown.innerHTML = ""
                hideDropdown()
            }
        }
    }

    input?.addEventListener("input", {
        scheduleTypeahead((it.target as? HTMLInputElement)?.value.orEmpty())
    })

    input?.addEventListener("keydown", { event ->
        val value = (event.target as? HTMLInputElement)?.value
        when ((event as KeyboardEvent).keyCode) {
            13 -> if (!value.isNullOrBlank()) navigateToSearch(value)  // Enter -> full results page
            27 -> hideDropdown()  // Escape
        }
    })

    input?.addEventListener("focus", { showDropdown() })
    // Delay the hide so a click on a suggestion registers before the dropdown is removed.
    input?.addEventListener("blur", {
        appScope.launch {
            delay(150.milliseconds)
            hideDropdown()
        }
    })

    // Keep the portalled dropdown aligned with the input as the page scrolls or resizes.
    val reposition: (org.w3c.dom.events.Event) -> Unit = {
        if (dropdown.style.display == "block") {
            positionDropdown()
        }
    }
    window.addEventListener("scroll", reposition, true)
    window.addEventListener("resize", reposition)

    element.querySelector("span")?.addEventListener("click", {
        input?.value?.takeIf { it.isNotBlank() }?.let {
            navigateToSearch(it)
        }
    })

    val searchText = document.location?.href?.let { URL(it) }?.searchParams?.get("searchText")
    if (searchText != null) {
        input?.value = searchText
    }

    input?.focus()
}

/**
 * Executes a search and sets the results
 */
fun searchResults(element: Element) {
    val query = element.getAttribute("data-search-input")
    if (!query.isNullOrBlank()) {
        appScope.launch {
            val resultsContainer = element.querySelector(".search-results-container")
            var done = false

            launch(Dispatchers.Main) {
                delay(700.milliseconds)
                if (!done) {
                    resultsContainer?.append {
                        span {
                            css {
                                fontSize = 2.em
                            }
                            unsafe {
                                +spinnerSvg()
                            }
                        }
                    }
                }
            }

            val result = Account.load("/search/results?searchText=${encodeURIComponent(query)}")
            done = true
            if (result != null) {
                resultsContainer?.let {
                    it.innerHTML = result
                }
            }
        }
    }
}